import { useRouter } from "next/router";
import { GetServerSideProps, GetStaticPropsContext } from "next";
import Link from "next/link";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FloatingCart } from "../../components/Cart/FloatingCart/FloatingCart";
import { SearchForm } from "../../components/Hotels/SearchForm/SearchForm";
import { List } from "../../components/List/List";
import { HotelViewPicker } from "../../components/List/ListHotelItem/HotelViewPicker/HotelViewPicker";
import { ListRoomItem } from "../../components/List/ListHotelItem/ListRoomItem/ListRoomItem";
import { Usps } from "../../components/Hotels/Usps/Usps";
import { ContinueLink } from "../../components/Misc/ContinueLink/ContinueLink";
import { HotelSearchRoom, HotelType, Room } from "../../types/HotelType";
import { getHotelSlug } from "../../utils/slugUtils";
import { HotelListModal } from "../../components/Hotels/HotelListModal";
import { CartContext } from "../../contexts/CartContext";
import { HotelSearchContext, HotelSearchContextType } from "../../contexts/HotelSearchContext";
import { HotelAPI } from "../../api/HotelAPI";
import { DatesSearchContext, DatesSearchContextType } from "../../contexts/DatesSearchContext";

import styles from "./hotel_slug.module.scss";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { guestNamePluralizer } from "../../utils/namePluralizer";
import { Hotel_SlugContext, Hotel_SlugContextType } from "../../contexts/Hotel_SlugContext";
import { CartAPI } from "../../api/CartAPI";
import { PageView } from "../../components/Analytics/PageView/PageView";
import Head from "next/head";


function HotelsDetails({hotel}: {hotel: HotelType}) {
  const router = useRouter();
  const { hotel_slug } = router.query;

  // Defaulting sort to low-high
  const defaultSort = 'ASC';

  const [priceSort, setPriceSort] = useState<string>(defaultSort);
  const [rooms, setRooms] = useState<Room[]>(hotel.rooms);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasPricesAfterSearch, setHasPricesAfterSearch] = useState<boolean>(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [linkLoading, setLinkLoading] = useState<boolean>(false);
  const [showCartPopUp, setShowCartPopUp] = useState<boolean>(false);

  const { setHotel_SlugToContext } = useContext<Hotel_SlugContextType>(Hotel_SlugContext);
  const { datesSearch } = useContext<DatesSearchContextType>(DatesSearchContext);
  const { hotelSearchRooms } = useContext<HotelSearchContextType>(HotelSearchContext);

  const handleSelect = (index: number) => {
    setTabIndex(index);
  }

  const handleLinkLoading = () => {
    setLinkLoading(true);
  }

  const handleSortByPriceUpdate = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget;
    const sortedRooms = [...rooms];
    setPriceSort(value);

    switch (value) {
      case 'ASC':
        return (sortedRooms.map((room) => {
          return room.rooms.sort((a, b) => +a.price - +b.price);
        }))
      case 'DESC':
        return (sortedRooms.map((room) => {
          return room.rooms.sort((a, b) => +b.price - +a.price);
        }))
      default:
        return sortedRooms;
    }
  }
  // Checking if any of the rooms coming back with a 0 price
  const hasPrices = (rooms: Room[]): boolean => {
    if (rooms !== undefined) {
      return rooms.some((room) => {
        return room.rooms.some((room) => {
          return +room.price > 0;
        })
      })
    } else {
      return false
    }
  }

  const searchForRooms = useCallback(async (datesSearch: Date[], hotelSearchRooms: HotelSearchRoom[], cartAPI?: CartAPI) => {
    setLoading(true);
    cartAPI?.clear();
    if (hotelSearchRooms.length > 0 && datesSearch.length > 0) {
      const roomsSearchQuery = new HotelAPI('' ?? []);
      const availableRooms = await roomsSearchQuery.getHotelRoomsAvailability(hotel.id, datesSearch, hotelSearchRooms)
      setRooms(availableRooms);
      setHasPricesAfterSearch(hasPrices(availableRooms));
      setShowCartPopUp(true)
    }
    else {
      setRooms(hotel.rooms);
      setHasPricesAfterSearch(true);
      setShowCartPopUp(false)
    }
    setLoading(false);
  }, [hotel]);


  const initialised = useRef(false);
  useEffect(() => {
    setLoading(true);
    setHotel_SlugToContext(hotel_slug as string);
    if (!initialised.current) {
      initialised.current = true;
      searchForRooms(datesSearch, hotelSearchRooms);
    }
    if (!hasPricesAfterSearch) {
      setShowCartPopUp(false)
    }
    setLinkLoading(false)
    setLoading(false)
  }, [initialised, searchForRooms, datesSearch, hotelSearchRooms, setHotel_SlugToContext, hotel_slug, rooms, hasPricesAfterSearch]);

  const nonPremierNids = [
    "323328",
    "323001",
    "323013",
    "323201",
    "235989",
    "224854",
    "224860",
    "224867",
    "224873",
  ]

  return <>
    <Head key={'hotel-room'}><title>Universal Orlando Resort™ | Your Orlando Vacation Destination | {hotel.title}</title></Head>
    <PageView pageName={"Hotel rooms"} roomsOnPage={rooms}/><CartContext.Consumer>
    {({ cartAPI, cart }) => {
      return (
        <main className="products">
          <FloatingCart showCartPopUp={showCartPopUp} currentSection="rooms" placement="top" />
          <ContinueLink currentSection="" />
          <p className={styles.linkholder}>
            <Link href={`/hotels`} legacyBehavior>
              {!linkLoading ? <a onClick={handleLinkLoading} className={styles.link}>{'< SEE ALL HOTELS'}</a> : <a className='loading mh10'></a>}
            </Link>
          </p>
          <List title={hotel.title} hero_image={hotel.img_hero} rating={hotel.star_rating} introText={hotel.desc_short}>
              <HotelListModal hotel={hotel} roomsPage={true} />
            {/* Don't show USPS non Premier hotels */}
            {nonPremierNids.includes(hotel.id) ? '' : <Usps hotelId={hotel.id}/>}
            <SearchForm setTabIndex={setTabIndex} cartAPI={cartAPI} onSubmit={searchForRooms} pageName={'Hotel Rooms'}/>
            {rooms === undefined ? <div className={styles.text}>We are having difficulties getting you hotels, Apologies for any inconvenience</div>
              :
              !loading && rooms.length ? (
                <>
                  {hasPricesAfterSearch ? (
                    <>
                      <HotelViewPicker
                        rooms={rooms}
                      >
                        <select aria-label="Sort by price" className={styles.sortBy} style={hasPrices(rooms) ? { display: 'block' } : { display: 'none' }} onChange={handleSortByPriceUpdate} defaultValue={priceSort}>
                          <option value="ASC">Price: Low to High</option>
                          <option value='DESC'>Price: High to Low</option>
                        </select>
                      </HotelViewPicker>
                      <Tabs selectedIndex={tabIndex} onSelect={handleSelect}>
                        {rooms.length > 1 ? (
                          <TabList className={styles.tabList}>
                            {rooms.map((roomsGroup) => {
                              return (
                                <Tab key={roomsGroup.room_number}>
                                  <h2>Room {roomsGroup.room_number}</h2>
                                  <h3>{guestNamePluralizer(roomsGroup)}</h3>
                                </Tab>
                              );
                            })}
                          </TabList>
                        ) : <TabList></TabList>}
                        {rooms.map((roomsGroup) => {
                          return (
                            <TabPanel key={roomsGroup.room_number} className={styles.tabPanelList}>
                              {roomsGroup.rooms.map((roomGroup, i) => {
                                return (
                                  <ListRoomItem tabIndex={tabIndex} setTabIndex={setTabIndex} hotelSearchRooms={hotelSearchRooms} key={roomGroup.product_id} hotel={hotel} room={roomGroup} roomNumber={roomsGroup.room_number} cart={cart} cartAPI={cartAPI} />
                                );
                              })}
                            </TabPanel>
                          );
                        })}
                      </Tabs></>
                  ) : <div className={styles.noResults}>There are no rooms available for the selected dates</div>}
                </>
              ) : <div className='loading mh100'></div>}
          </List>
          <FloatingCart showCartPopUp={showCartPopUp} currentSection="rooms" placement="bottom" />
        </main>
      );
    } }
  </CartContext.Consumer></>;

}

interface serverSideProps {
  hotel: HotelType,
}

export const getServerSideProps: GetServerSideProps<serverSideProps> = async (context: GetStaticPropsContext) => {

  // get all hotels id and titles form the api
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/hotels?resort=588fields=id,title&cached=1`);

  const hotels = await res.json();



  // error handling for the api
  if (res.status !== 200) {
    return {
      notFound: true
    }
  }

  // Get all hotels ids and titles
  let hotelId = ''
  hotels.data.forEach((hotel: HotelType) => {
    if (getHotelSlug(hotel.title) === context!.params!.hotel_slug) {
      hotelId = hotel.id;
    }
  })


  let hotel = []
  if (hotelId) {
    const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/hotels/${hotelId}cached=1`);
    hotel = await res.json();
  }
  return { props: { hotel } }
}




export default HotelsDetails;
